import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as activityActions from "../../data/actions/activityActions";
import { getPersonsByAssociation } from "../../data/actions/personActions";
import { IState } from "../../data/reducers/state";
import {
  ILoginActivityType,
  IRosterGameActivity,
  IShiftUpdateActivityType,
  IUpdateAppointmentActivityType,
  IUserActivity,
} from "../../types/activity/Activity";
import { ActivityType } from "../../types/activity/ActivityType";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { IPerson, IPersonLite } from "../../types/persons/person";
import { toDateTime, toDateTimeNullable } from "../../util/date+helper";
import { GameLink, PersonLink } from "../common/Links";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";

interface ActivitesPageProps {
  actions: {
    getMyActivities: () => void;
  };
  activities: IUserActivity<any>[];
  self: IPerson;
  persons: { [id: string]: IPersonLite };
}

function prepareSubtitleText(
  activity: IUserActivity<any>,
  person: IPersonLite,
  payloadPerson?: IPersonLite | null,
  isMe?: boolean,
) {
  const youOrPerson =
    isMe === false || !person
      ? "You"
      : person.preferredName || person.firstName;
  const yourOrTheir = isMe === false || !person ? "your" : "their";
  switch (activity.type) {
    case ActivityType.loginSuccess:
      return (
        <p>
          {`${youOrPerson} logged in with the IP address: ${(activity as IUserActivity<ILoginActivityType>).payload.ipAddress
            }.`}
        </p>
      );
    case ActivityType.loginFailed:
      return (
        <p>
          {`There was an unsuccessful login attempt from the IP address: ${(activity as IUserActivity<ILoginActivityType>).payload.ipAddress
            }.`}
        </p>
      );
    case ActivityType.logout:
      return <p>{`${youOrPerson} logged out from a session.`}</p>;
    case ActivityType.changePassword:
      return <p>{`${youOrPerson} changed ${yourOrTheir} password.`}</p>;
    case ActivityType.rosterGame: {
      const a: IUserActivity<IRosterGameActivity> = activity;
      const rosteredPerson: null | { fullName: string } = payloadPerson || null;
      if (rosteredPerson) {
        return (
          <p>
            {`${youOrPerson} rostered ${rosteredPerson.fullName} to a `}
            <GameLink gameId={a.payload.gameId}>game</GameLink>.
          </p>
        );
      }
      return (
        <p>
          {`${youOrPerson} removed someone from a `}
          <GameLink gameId={a.payload.gameId}>game</GameLink>.
        </p>
      );
    }
    case ActivityType.updateShift: {
      const a: IUserActivity<IShiftUpdateActivityType> = activity;
      return (
        <p>
          {`${youOrPerson} updated the shift:with changed fields: ${JSON.stringify(
            a.payload.shift,
          )}.`}
        </p>
      );
    }
    case ActivityType.updateAppointmentStatus: {
      const a: IUserActivity<IUpdateAppointmentActivityType> = activity;
      return (
        <p>
          {`${youOrPerson} ${AppointmentStatus.toString(a.payload.status)} an `}
          <a>activity</a>
        </p>
      );
    }
    case ActivityType.resetPasswordRequestAnon: {
      return (
        <p>
          ${activity.payload.ip} requested a password reset for $
          {person?.fullName || activity._id}
        </p>
      );
    }
    default:
      return <p>An unsupported activity type was logged.</p>;
  }
}

class ActivitiesPage extends React.PureComponent<ActivitesPageProps, {}> {
  constructor(props: ActivitesPageProps) {
    super(props);
    const person: IPerson = props.self;
    props.actions.getMyActivities();
    props.actions.getPersonsByAssociation(person.associations[0].associationId);
  }

  render() {
    const personMap = this.props.persons;
    return (
      <div className="p-3">
        <h1>Activities</h1>
        {(!this.props.activities || this.props.activities.length === 0) && (
          <CenteredLoader />
        )}
        <div style={{ padding: "1rem" }}>
          <SSTable style={{ overflowX: "scroll", display: "block" }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Person</th>
                <th>Time</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {(this.props.activities || []).map((a) => {
                const person = personMap[a.person];
                let payloadPerson;
                if (a.type == ActivityType.rosterGame && a.payload.referee) {
                  payloadPerson = personMap[a.payload.referee];
                }

                return (
                  <tr key={a._id}>
                    <td>{ActivityType.toDisplayString(a.type)}</td>
                    <td>
                      <PersonLink identifier={person?.externalIdentifier}>
                        {person ? person.fullName : a.person}
                      </PersonLink>
                    </td>
                    <td>
                      {toDateTimeNullable(a.timestamp)?.toLocaleString() || ""}
                    </td>
                    <td>{prepareSubtitleText(a, person, payloadPerson)}</td>
                  </tr>
                );
              })}
            </tbody>
          </SSTable>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    self: state.persons.map[state.persons.self],
    activities: state.activities.activities,
    persons: state.persons.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { ...activityActions, getPersonsByAssociation },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ActivitiesPage);
